import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, ArrowLeft } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';
import { getCorridor } from '../../data/corridors';
import { toArabicSlug } from '../../data/areaSlugMapping';
import './CorridorDetail.css';

const CorridorDetail = () => {
  const { corridorId } = useParams();
  const c = getCorridor(decodeURIComponent(corridorId || ''));

  useEffect(() => { window.scrollTo(0, 0); }, [corridorId]);

  if (!c) {
    return (
      <div className="corridor-detail-section" style={{ minHeight: '60vh', padding: '4rem 1rem', textAlign: 'center' }}>
        <SEO
          title="المحور غير موجود"
          description="المحور المطلوب غير موجود في قاعدة البيانات. تواصل مع ونش انقاذ محمد بحر على 01044844492."
          path={`/corridor/${encodeURIComponent(corridorId || '')}`}
          noindex
        />
        <h1 style={{ color: '#0a2c61' }}>المحور غير موجود</h1>
        <p>تواصل معنا على <a href="tel:+201044844492">01044844492</a> لخدمتك.</p>
        <Link to="/areas" style={{ color: '#1976d2' }}>العودة للمناطق</Link>
      </div>
    );
  }

  return (
    <section className="corridor-detail-section" style={{ minHeight: '100vh', background: '#f7f7f7', fontFamily: 'Tajawal, Arial, sans-serif' }}>
      <SEO
        title={`${c.name} | خدمة 24 ساعة - خصم 50%`}
        description={c.metaDescription}
        path={`/corridor/${encodeURIComponent(c.id)}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: c.name,
          description: c.intro,
          provider: { "@type": "AutomotiveBusiness", name: "ونش انقاذ سيارات محمد بحر", telephone: "+201044844492" },
          areaServed: { "@type": "Country", name: "مصر" },
          url: `https://winchmohamedbahr.com/corridor/${encodeURIComponent(c.id)}`,
        }}
      />

      <Breadcrumb
        items={[{ label: "المناطق", path: "/areas" }, { label: c.id, path: `/corridor/${encodeURIComponent(c.id)}` }]}
        path={`/corridor/${encodeURIComponent(c.id)}`}
      />

      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        <div id="header" style={{ background: 'linear-gradient(135deg, #0a2c61, #1976d2)', color: 'white', padding: '2rem', borderRadius: '1.5rem', textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', margin: 0, direction: 'rtl' }}>{c.name}</h1>
          <p style={{ margin: '0.5rem 0 0', opacity: 0.9 }}>وقت الاستجابة التقريبي: {c.responseTime}</p>
        </div>

        <div id="staging" style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
          <h2 style={{ color: '#0a2c61', marginTop: 0 }}>📍 نقاط تجمع أوناشنا</h2>
          <ul style={{ direction: 'rtl' }}>
            {c.staging.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>

        <div id="exits" style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
          <h2 style={{ color: '#0a2c61', marginTop: 0 }}>🛣️ المخارج والمدخلات المغطاة</h2>
          <ul style={{ direction: 'rtl' }}>
            {c.exits.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>

        <div id="danger" style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
          <h2 style={{ color: '#0a2c61', marginTop: 0 }}>⚠️ مناطق الخطر الشائعة</h2>
          <ul style={{ direction: 'rtl' }}>
            {c.dangerZones.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        </div>

        {c.equipment && c.equipment.length > 0 && (
          <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <h2 style={{ color: '#0a2c61', marginTop: 0 }}>🔧 المعدات المستخدمة</h2>
            <ul style={{ direction: 'rtl' }}>
              {c.equipment.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}

        {c.process && c.process.length > 0 && (
          <div id="process" style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <h2 style={{ color: '#0a2c61', marginTop: 0 }}>إجراءات الخدمة</h2>
            <ol style={{ direction: 'rtl' }}>
              {c.process.map((p, i) => <li key={i}>{p}</li>)}
            </ol>
          </div>
        )}

        {c.safety && c.safety.length > 0 && (
          <div id="safety" style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <h2 style={{ color: '#0a2c61', marginTop: 0 }}>🛡️ إجراءات السلامة</h2>
            <ul style={{ direction: 'rtl' }}>
              {c.safety.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
        )}

        {c.tolls && c.tolls.length > 0 && (
          <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <h2 style={{ color: '#0a2c61', marginTop: 0 }}>💰 الرسوم</h2>
            <ul style={{ direction: 'rtl' }}>
              {c.tolls.map((t, i) => <li key={i}>{t}</li>)}
            </ul>
          </div>
        )}

        {c.relatedAreas && c.relatedAreas.length > 0 && (
          <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', marginBottom: '1.5rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
            <h2 style={{ color: '#0a2c61', marginTop: 0 }}>🗺️ المناطق المغطاة على المحور</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', direction: 'rtl' }}>
              {c.relatedAreas.map((a, i) => (
                <Link key={i} to={`/areas/${toArabicSlug(a)}`} style={{ background: '#0a2c61', color: 'white', padding: '0.4rem 0.9rem', borderRadius: '0.5rem', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>
                  ونش {a}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div id="contact" style={{ background: '#ffc107', padding: '2rem', borderRadius: '1rem', textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ color: '#0a2c61', marginTop: 0 }}>📞 اطلب ونش الآن — خصم 50%</h2>
          <p style={{ color: '#0a2c61', marginBottom: '1rem' }}>متاحون 24/7 على طريق {c.id}. سعر ابتدائي 450 ج.م.</p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="tel:+201044844492" style={ctaStyle}><Phone size={18} /> اتصل: 01044844492</a>
            <a href="https://wa.me/201044844492" style={{ ...ctaStyle, background: '#25D366' }}><FontAwesomeIcon icon={faWhatsapp} /> واتساب</a>
          </div>
        </div>

        <div style={{ textAlign: 'center', padding: '1rem 0 3rem' }}>
          <Link to="/areas" style={{ color: '#0a2c61', fontWeight: 700, textDecoration: 'none' }}>
            <ArrowLeft size={18} /> العودة لجميع المناطق
          </Link>
        </div>
      </div>
    </section>
  );
};

const ctaStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  background: '#0a2c61',
  color: 'white',
  padding: '0.7rem 1.3rem',
  borderRadius: '0.7rem',
  textDecoration: 'none',
  fontWeight: 700,
  fontSize: '1rem',
};

export default CorridorDetail;
